<template>
  <div class="payment-conditions">
    <component
      v-for="(option, index) in accommodationsWithPaymentConditions"
      :key="`${option.prestId}-${index}`"
      :is="getPaymentComponent(option.paymentCondition as PaymentCondition)"
      :prestId="option.prestId"
      :cancellation-until="option.cancellationUntil"
      :lihe-id="props.liheId"
      :lihe-name="props.liheName"
      :package-code="props.packageCode"
      class="mb-2"
    />
  </div>
</template>

<script lang="ts" setup>
  // Vue
  import { Component, computed, ComputedRef, defineProps } from 'vue'

  // Types
  import {
    OptionSummary,
    PaymentCondition,
  } from '@/services/BookingService.interface'

  // Components
  import FixedPaymentCondition from './FixedPaymentCondition.vue'
  import FlexiblePaymentCondition from './FlexiblePaymentCondition.vue'

  // Config
  import { PrestType } from '@/config'

  // Props
  interface Props {
    options: OptionSummary[]
    liheId: number
    liheName?: string
    packageCode?: string
  }

  const props = defineProps<Props>()

  // Filter accommodations that have valid payment conditions
  const accommodationsWithPaymentConditions: ComputedRef<OptionSummary[]> = computed(() => {
    if (!props.options) throw new Error('No accommodations in computed')

    // Filter by option type to get accommodations
    const accommodations = props.options.filter(
      (option: OptionSummary) => option.prestTypeId === PrestType.Accomodation
    )

    // Filter out accommodations without valid payment conditions and log warnings
    const validAccommodations = accommodations.filter((option: OptionSummary) => {
      if (option.paymentCondition === null || option.paymentCondition === undefined) {
        console.warn(`Accommodation ${option.prestId} (${option.name}) has null/undefined payment condition. Skipping payment condition display.`)
        return false
      }
      
      if (option.paymentCondition !== PaymentCondition.FIXED && option.paymentCondition !== PaymentCondition.FLEXIBLE) {
        console.warn(`Accommodation ${option.prestId} (${option.name}) has invalid payment condition: ${option.paymentCondition}. Expected ${PaymentCondition.FIXED} (FIXED) or ${PaymentCondition.FLEXIBLE} (FLEXIBLE).`)
        return false
      }
      
      return true
    })

    return validAccommodations
  })

  // Methods
  const getPaymentComponent = (
    paymentCondition: PaymentCondition
  ): Component => {
    if (paymentCondition === PaymentCondition.FIXED) {
      return FixedPaymentCondition
    } else if (paymentCondition === PaymentCondition.FLEXIBLE) {
      return FlexiblePaymentCondition
    } else {
      // This should not happen due to our filtering, but provide fallback
      return FlexiblePaymentCondition
    }
  }
</script>

<style scoped></style>
